<template>
    <div class="editor-toolbar">
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.bold')"
            :active="editor.isActive('bold')"
            icon="fas fa-bold"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleBold().run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.italic')"
            :active="editor.isActive('italic')"
            icon="fas fa-italic"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleItalic().run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.underline')"
            icon="fas fa-underline"
            :active="editor?.isActive('underline')"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleUnderline().run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.strike')"
            :active="editor.isActive('strike')"
            icon="fas fa-strikethrough"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleStrike().run();
                    }
                }
            "
        />
        <EditorColorPicker
            v-if="editor"
            :title="i18n('voucherDesign.toolbar.textColor')"
            icon="fas fa-palette"
            v-model="textColor"
            :showColorCode="false"
        />
        <span class="separator"></span>
        <EditorMenuDropdown
            :title="i18n('voucherDesign.toolbar.textType')"
            icon="fas fa-heading"
            :label="headingLabel"
            :items="headingOptions"
        />
        <span class="separator"></span>
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.alignLeft')"
            icon="fas fa-align-left"
            :active="editor.isActive({ textAlign: 'left' })"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleTextAlign('left').run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.alignCenter')"
            icon="fas fa-align-center"
            :active="editor.isActive({ textAlign: 'center' })"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleTextAlign('center').run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.alignRight')"
            icon="fas fa-align-right"
            :active="editor.isActive({ textAlign: 'right' })"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleTextAlign('right').run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.alignJustify')"
            icon="fas fa-align-justify"
            :active="editor.isActive({ textAlign: 'justify' })"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleTextAlign('justify').run();
                    }
                }
            "
        />
        <span class="separator"></span>
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.bulletList')"
            :active="editor.isActive('bulletList')"
            icon="fas fa-list-ul"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleBulletList().run();
                    }
                }
            "
        />
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.orderedList')"
            :active="editor.isActive('orderedList')"
            icon="fas fa-list-ol"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().toggleOrderedList().run();
                    }
                }
            "
        />
        <span class="separator"></span>
        <!-- <EditorMenuButton
            title="Blockquote"
            :active="editor.isActive('blockquote')"
            icon="fas fa-quote-left"
            @click="editor.chain().focus().toggleBlockquote().run()"
        /> -->
        <EditorMenuButton
            :title="i18n('voucherDesign.toolbar.horizontalRule')"
            :active="editor.isActive('horizontalRule')"
            icon="fas fa-horizontal-rule"
            @click="
                () => {
                    if (isInsideAbsoluteTextBox(editor)) {
                        editor.chain().focus().setHorizontalRule().run();
                    }
                }
            "
        />
        <!-- <EditorMenuButton
            title="Undo"
            :active="editor.isActive('undo')"
            icon="fas fa-undo"
            @click="editor.chain().focus().undo().run()"
        />
        <EditorMenuButton
            title="Redo"
            :active="editor.isActive('redo')"
            icon="fas fa-redo"
            @click="editor.chain().focus().redo().run()"
        /> -->
    </div>
</template>

<script setup lang="ts">
    import { i18n } from "@/portal/Trans";
    import type { Editor } from "@tiptap/vue-3";
    import { computed, ref, watch } from "vue";

    import EditorColorPicker from "./EditorColorPicker.vue";
    import EditorMenuButton from "./EditorMenuButton.vue";
    import EditorMenuDropdown, { type DropdownItem } from "./EditorMenuDropdown.vue";

    import { isInsideAbsoluteTextBox } from "./helpers/editorUtils";

    interface Props {
        editor: Editor;
    }

    const props = defineProps<Props>();

    const textColor = ref("#000000");
    watch(textColor, (newColor) => {
        if (isInsideAbsoluteTextBox(props.editor)) {
            props.editor.chain().focus().setColor(newColor).run();
        }
    });

    const headingLabel = computed(() => {
        if (!props.editor) return i18n("voucherDesign.toolbar.paragraph");
        if (props.editor.isActive("heading", { level: 1 })) return i18n("voucherDesign.toolbar.heading1");
        if (props.editor.isActive("heading", { level: 2 })) return i18n("voucherDesign.toolbar.heading2");
        if (props.editor.isActive("heading", { level: 3 })) return i18n("voucherDesign.toolbar.heading3");
        if (props.editor.isActive("heading", { level: 4 })) return i18n("voucherDesign.toolbar.heading4");
        if (props.editor.isActive("heading", { level: 5 })) return i18n("voucherDesign.toolbar.heading5");
        return i18n("voucherDesign.toolbar.paragraph");
    });

    const headingOptions = computed<DropdownItem[]>(() => {
        if (!props.editor) return [];

        return [
            {
                label: i18n("voucherDesign.toolbar.paragraph"),
                active: props.editor.isActive("paragraph"),
                command: () => {
                    if (isInsideAbsoluteTextBox(props.editor)) {
                        props.editor.chain().focus().setParagraph().run();
                    }
                },
            },
            {
                label: i18n("voucherDesign.toolbar.heading1"),
                active: props.editor.isActive("heading", { level: 1 }),
                command: () => {
                    if (isInsideAbsoluteTextBox(props.editor)) {
                        props.editor.chain().focus().toggleHeading({ level: 1 }).run();
                    }
                },
            },
            {
                label: i18n("voucherDesign.toolbar.heading2"),
                active: props.editor.isActive("heading", { level: 2 }),
                command: () => {
                    if (isInsideAbsoluteTextBox(props.editor)) {
                        props.editor.chain().focus().toggleHeading({ level: 2 }).run();
                    }
                },
            },
            {
                label: i18n("voucherDesign.toolbar.heading3"),
                active: props.editor.isActive("heading", { level: 3 }),
                command: () => {
                    if (isInsideAbsoluteTextBox(props.editor)) {
                        props.editor.chain().focus().toggleHeading({ level: 3 }).run();
                    }
                },
            },
            {
                label: i18n("voucherDesign.toolbar.heading4"),
                active: props.editor.isActive("heading", { level: 4 }),
                command: () => {
                    if (isInsideAbsoluteTextBox(props.editor)) {
                        props.editor.chain().focus().toggleHeading({ level: 4 }).run();
                    }
                },
            },
            {
                label: i18n("voucherDesign.toolbar.heading5"),
                active: props.editor.isActive("heading", { level: 5 }),
                command: () => {
                    if (isInsideAbsoluteTextBox(props.editor)) {
                        props.editor.chain().focus().toggleHeading({ level: 5 }).run();
                    }
                },
            },
        ];
    });
</script>

<style lang="scss">
    .editor-toolbar {
        margin-bottom: 1rem;
        display: flex;
        gap: 0.4rem;

        button {
            border: 1px solid #ccc;
            background: white;
            padding: 0.3rem 0.5rem;
            cursor: pointer;
            font-size: 1rem;
            display: flex;
            align-items: center;
            gap: 0.3rem;
            border-radius: 0.3rem;

            &.active {
                background-color: #eee;
                border-color: #888;
            }

            i {
                pointer-events: none;
            }
        }

        .separator {
            color: #ccc;
            display: inline-block;
            width: 1px;
            background-color: #ccc;
            height: 30px;
        }
    }
</style>
